// REPORTDASH1 (docs/build-briefs/REPORTDASH1-reports-dashboard.md; design/mockups/reports-dashboard-concepts/b.html and c.html):
// the Reports dashboard, one component for the location, franchisor and Snip & Sip Admin consoles. A Needs attention band (only
// when something needs attention), the key-number strip, the pinned report previews, and the Customize bottom sheet. The host
// supplies a `DashboardSource` (which components exist and the words their data becomes); this file draws them the same way in
// every console. Touch first: no drag, no hover-only, every control 44px or taller. Color follows DEC-533: figures and chart
// marks are neutral; the accent is on buttons only; status always carries words and an icon.
import {
  ATTENTION_LIMIT,
  MAX_FIGURES,
  MAX_PREVIEWS,
  addId,
  layoutKey,
  moveId,
  readLayout,
  removeId,
  writeLayout,
  type AttentionRow,
  type Chip,
  type DashLoad,
  type DashboardSource,
  type FigureDef,
  type FigureView,
  type Layout,
  type OpenTarget,
  type PreviewBody,
  type PreviewDef,
  type PreviewView,
} from "./reportsDashboardModel.ts";
import type { TileFigure } from "./reportTiles.ts";

export interface TileItem {
  id: string;
  available: boolean;
  definitionId?: string;
  label?: string;
}
/** CRB10: the person's pinned one-number reports, through the existing tile routes. */
export interface TilesApi {
  list(): Promise<TileItem[] | undefined>;
  run(id: string): Promise<TileFigure | "unavailable" | undefined>;
  order(ids: string[]): Promise<boolean>;
  remove(id: string): Promise<boolean>;
  open(definitionId: string): void;
  canManage: boolean;
}
export interface DashboardContext {
  source: DashboardSource;
  /** True when this person can open the target (the catalog says the report can run, the card is offered, the host has the screen). */
  canOpen(target: OpenTarget, range?: { from: string; to: string }): boolean;
  open(target: OpenTarget, range?: { from: string; to: string }): void;
  tiles?: TilesApi;
  active(): boolean;
  storage?: Pick<Storage, "getItem" | "setItem">;
}
export interface ReportsDashboard {
  element: HTMLElement;
  load(): Promise<void>;
  /** Opens the Customize sheet. Focus goes back to the control `restoreFocus` finds when it closes. */
  customize(restoreFocus?: () => HTMLElement | null): void;
  destroy(): void;
}

interface TileView {
  item: TileItem;
  figure: TileFigure | "loading" | "unavailable" | "failed";
}

const SVG_NS = "http://www.w3.org/2000/svg";
const ICONS: Record<string, string> = {
  clock: "M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z",
  check: "M5 12.5l4.5 4.5L19 7.5",
  live: "M12 12m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0M7.5 7.5a6.4 6.4 0 0 0 0 9M16.5 7.5a6.4 6.4 0 0 1 0 9",
  count: "M4 6h16M4 12h16M4 18h10",
  warn: "M12 4l9 16H3zM12 10v4M12 17.2v.1",
  up: "M6 14l6-6 6 6",
  down: "M6 10l6 6 6-6",
  minus: "M6 12h12",
  plus: "M12 6v12M6 12h12",
  alert: "M12 4l9 16H3zM12 10v4M12 17.2v.1",
};
function icon(name: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("class", "rd-ic");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", ICONS[name] ?? "");
  svg.append(path);
  return svg;
}
function node<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== undefined) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}
function button(label: string, className: string, action: () => void, testid?: string): HTMLButtonElement {
  const element = node("button", className, label);
  element.type = "button";
  element.addEventListener("click", action);
  if (testid !== undefined) element.dataset["testid"] = testid;
  return element;
}
function chip(value: Chip): HTMLElement {
  const element = node("span", `rd-chip rd-chip-${value.kind}`);
  const mark = value.kind === "open" ? "clock" : value.kind === "exact" ? "check" : value.kind === "live" ? "live" : value.kind === "count" ? "count" : "warn";
  element.append(icon(mark), document.createTextNode(value.text));
  return element;
}

export function createReportsDashboard(context: DashboardContext): ReportsDashboard {
  const { source } = context;
  const storage = context.storage ?? window.localStorage;
  const key = layoutKey(source.storageKey);
  const readStored = (): string | null => {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  };
  let layout: Layout = readLayout(readStored(), source);
  let data: DashLoad | undefined;
  let status: "loading" | "ready" | "failed" = "loading";
  let tiles: TileView[] = [];
  let tilesLoaded = false;
  let sheet = false;
  let sheetTab: "figures" | "previews" = "figures";
  let sheetNote = "";
  let restoreFocus: (() => HTMLElement | null) | undefined;
  let generation = 0;
  let alive = true;

  const element = node("div", "rd");
  element.dataset["testid"] = "rd";
  // The page element is detached while a report is open, so only the host's own flag says whether a late reply still counts.
  const live = () => alive && context.active();

  function save(next: Layout): void {
    layout = next;
    try {
      storage.setItem(key, writeLayout(layout));
    } catch {
      sheetNote = "Your choices could not be saved on this browser, so they last until you leave this page.";
    }
    render();
  }

  // -------------------------------------------------------------------------------------------------------------------
  // What this person can see
  // -------------------------------------------------------------------------------------------------------------------
  const openable = (targets: OpenTarget[], range?: { from: string; to: string }): OpenTarget | undefined =>
    targets.find((target) => context.canOpen(target, range));
  const figureDefs = (): FigureDef[] =>
    source.figures.filter((def) => data?.figures[def.id] !== undefined && openable(def.open) !== undefined);
  const previewDefs = (): PreviewDef[] =>
    source.previews.filter((def) => {
      const view = data?.previews[def.id];
      return view !== undefined && openable(def.open, view.range) !== undefined;
    });
  const stripFigures = (): FigureDef[] => {
    const offered = new Map(figureDefs().map((def) => [def.id, def]));
    return layout.figures.map((id) => offered.get(id)).filter((def): def is FigureDef => def !== undefined);
  };
  const pinnedPreviews = (): PreviewDef[] => {
    const offered = new Map(previewDefs().map((def) => [def.id, def]));
    return layout.previews.map((id) => offered.get(id)).filter((def): def is PreviewDef => def !== undefined);
  };
  /** Moves one id among the ones this person can see; ids they cannot see stay in the list, after the visible ones. */
  const reorder = (list: readonly string[], visible: readonly string[], id: string, direction: -1 | 1): string[] => [
    ...moveId(visible, id, direction),
    ...list.filter((entry) => !visible.includes(entry)),
  ];
  const stripCount = () => stripFigures().length + tiles.length;

  // -------------------------------------------------------------------------------------------------------------------
  // Loading
  // -------------------------------------------------------------------------------------------------------------------
  async function load(): Promise<void> {
    const captured = ++generation;
    if (data === undefined) status = "loading";
    render();
    try {
      const next = await source.load();
      if (!live() || captured !== generation) return;
      data = next;
      status = "ready";
    } catch {
      if (!live() || captured !== generation) return;
      if (data === undefined) status = "failed";
    }
    render();
    void loadTiles(captured);
  }
  /** CRB10 tiles load after the page and run one at a time (CRB5-F1: one run per location at once, no automatic retry). */
  async function loadTiles(captured: number): Promise<void> {
    const api = context.tiles;
    if (api === undefined) return;
    let items: TileItem[] | undefined;
    try {
      items = await api.list();
    } catch {
      items = undefined;
    }
    if (!live() || captured !== generation) return;
    if (items === undefined) {
      if (!tilesLoaded) tiles = [];
      return;
    }
    tilesLoaded = true;
    tiles = items.map((item) => ({ item, figure: item.available ? "loading" : "unavailable" }));
    render();
    for (const tile of tiles) {
      if (!tile.item.available) continue;
      let figure: TileView["figure"];
      try {
        const result = await api.run(tile.item.id);
        figure = result === undefined ? "failed" : result;
      } catch {
        figure = "failed";
      }
      if (!live() || captured !== generation) return;
      tile.figure = figure;
      render();
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------------------------------------------------------
  function openTarget(targets: OpenTarget[], range?: { from: string; to: string }): () => void {
    return () => {
      const target = openable(targets, range);
      if (target !== undefined) context.open(target, range);
    };
  }
  function figureCell(def: FigureDef, view: FigureView): HTMLElement {
    const cell = node("div", `rd-cell rd-state-${view.state}`);
    cell.dataset["testid"] = `rd-figure-${def.id}`;
    const open = button(def.title, "rd-open rd-cell-title", openTarget(def.open), `rd-open-${def.id}`);
    open.setAttribute("aria-label", view.state === "ready" ? `${def.title}, ${view.value}, ${view.context}. Open the report` : `${def.title}. Open the report`);
    cell.append(open);
    cell.append(...tileBody(view));
    return cell;
  }
  function tileBody(view: FigureView): HTMLElement[] {
    if (view.state === "ready") {
      const foot = node("div", "rd-foot");
      if (view.chip !== undefined) foot.append(chip(view.chip));
      return [node("div", "rd-fig num", view.value), node("div", "rd-ctx", view.context), foot];
    }
    if (view.state === "failed") {
      const wrap = node("div", "rd-empty");
      wrap.append(node("b", undefined, view.emptyTitle), button("Try again", "rx-btn rd-retry-tile", () => void load(), "rd-retry-tile"));
      return [wrap];
    }
    const wrap = node("div", "rd-empty");
    wrap.append(node("b", undefined, view.emptyTitle), node("span", undefined, view.emptyText));
    return [wrap];
  }
  function tileCell(tile: TileView): HTMLElement {
    const cell = node("div", `rd-cell rd-state-${tile.figure === "loading" || typeof tile.figure === "string" ? "empty" : "ready"}`);
    cell.dataset["testid"] = `rd-tile-${tile.item.id}`;
    const title = tile.item.available ? (tile.item.label ?? "Report") : "No longer available to you";
    const definitionId = tile.item.definitionId;
    const open = button(title, "rd-open rd-cell-title", () => {
      if (definitionId !== undefined) context.tiles?.open(definitionId);
    });
    open.disabled = !tile.item.available || definitionId === undefined;
    cell.append(open);
    const figure = tile.figure;
    if (typeof figure === "object") {
      const foot = node("div", "rd-foot");
      foot.append(chip({ kind: "exact", text: figure.asOf === undefined ? "Saved report" : `As of ${figure.asOf}` }));
      cell.append(node("div", "rd-fig num", figure.value), node("div", "rd-ctx", [figure.delta, figure.span].filter(Boolean).join(", ")), foot);
    } else {
      const wrap = node("div", "rd-empty");
      if (figure === "loading") wrap.append(node("b", undefined, "Loading"));
      else if (figure === "unavailable") wrap.append(node("b", undefined, "No longer available to you"));
      else wrap.append(node("b", undefined, "Could not load"));
      cell.append(wrap);
    }
    return cell;
  }
  function chartBody(body: Extract<PreviewBody, { kind: "chart" }>): HTMLElement {
    const wrap = node("div", "rd-chart-wrap");
    const max = Math.max(1, ...body.points.map((point) => point.value));
    const chart = node("div", "rd-chart");
    chart.setAttribute("role", "img");
    chart.setAttribute("aria-label", `${body.first} to ${body.last}. ${body.summary}`);
    for (const point of body.points) {
      const mark = node("i", point.highlight ? "rd-mark today" : "rd-mark");
      mark.style.height = `${Math.max(2, Math.round((Math.max(0, point.value) / max) * 100))}%`;
      mark.title = "";
      chart.append(mark);
    }
    const axis = node("div", "rd-axis");
    axis.append(node("span", undefined, body.first), node("span", undefined, body.last));
    wrap.append(chart, axis);
    return wrap;
  }
  /** A grid of divs with table roles: the location console styles every `table` under Reports by id, so this stays clear of it. */
  function table(body: Extract<PreviewBody, { kind: "table" }>): HTMLElement {
    const grid = node("div", "rd-table");
    grid.setAttribute("role", "table");
    const columns = `minmax(0, 1fr) repeat(${Math.max(0, body.columns.length - 1)}, auto)`;
    const line = (cells: string[], header: boolean): HTMLElement => {
      const rowEl = node("div", header ? "rd-trow rd-thead" : "rd-trow");
      rowEl.setAttribute("role", "row");
      rowEl.style.gridTemplateColumns = columns;
      for (const cellText of cells) {
        const cellEl = node("span", undefined, cellText);
        cellEl.setAttribute("role", header ? "columnheader" : "cell");
        rowEl.append(cellEl);
      }
      return rowEl;
    };
    grid.append(line(body.columns, true));
    for (const row of body.rows) grid.append(line(row, false));
    return grid;
  }
  function previewCard(def: PreviewDef, view: PreviewView): HTMLElement {
    const card = node("article", `rx-card rd-pv rd-state-${view.state}`);
    card.dataset["testid"] = `rd-preview-${def.id}`;
    const open = button(def.title, "rd-open rd-pv-title", openTarget(def.open, view.range), `rd-open-${def.id}`);
    open.setAttribute("aria-label", `${def.title}. Open the report`);
    card.append(open);
    if (view.filters.length > 0) {
      const filters = node("div", "rd-filters");
      for (const label of view.filters) filters.append(node("span", undefined, label));
      card.append(filters);
    }
    if (view.state === "ready" && view.body !== undefined) {
      card.append(view.body.kind === "chart" ? chartBody(view.body) : table(view.body));
      const foot = node("div", "rd-foot");
      if (view.chip !== undefined) foot.append(chip(view.chip));
      if (view.body.kind === "chart") foot.append(node("span", "rd-note", view.body.summary));
      card.append(foot);
    } else if (view.state === "failed") {
      const wrap = node("div", "rd-empty");
      wrap.append(node("b", undefined, view.emptyTitle), button("Try again", "rx-btn rd-retry-tile", () => void load(), "rd-retry-preview"));
      card.append(wrap);
    } else {
      const wrap = node("div", "rd-empty");
      wrap.append(node("b", undefined, view.emptyTitle), node("span", undefined, view.emptyText));
      card.append(wrap);
    }
    return card;
  }
  function attentionBand(rows: AttentionRow[] | "failed"): HTMLElement | undefined {
    if (rows !== "failed" && rows.length === 0) return undefined;
    const band = node("section", "rd-attn");
    band.dataset["testid"] = "rd-attention";
    band.setAttribute("aria-label", "Needs attention");
    band.append(node("h2", "rx-section", "Needs attention"));
    const list = node("div", "rx-card rd-attn-list");
    if (rows === "failed") {
      const row = node("div", "rd-attn-row");
      row.setAttribute("role", "status");
      row.append(icon("alert"), node("div", "rd-attn-text", "Needs attention could not load."), button("Try again", "rx-btn", () => void load(), "rd-attention-retry"));
      list.append(row);
    } else {
      for (const item of rows.slice(0, ATTENTION_LIMIT)) {
        const row = node("div", "rd-attn-row");
        row.dataset["testid"] = `rd-attention-${item.id}`;
        const text = node("div", "rd-attn-text");
        text.append(node("b", undefined, item.cause), node("span", undefined, item.consequence));
        row.append(icon("alert"), text);
        const action = item.action;
        if (action !== undefined) {
          row.append(button(action.label, "rx-btn rd-attn-action", () => context.open(action.target), `rd-attention-action-${item.id}`));
        } else if (item.whoCanFix !== undefined) row.append(node("span", "rd-attn-who", item.whoCanFix));
        list.append(row);
      }
      if (rows.length > ATTENTION_LIMIT) list.append(node("div", "rd-attn-more", `${rows.length - ATTENTION_LIMIT} more not shown.`));
    }
    band.append(list);
    return band;
  }

  function render(): void {
    if (!alive) return;
    element.replaceChildren();
    element.classList.toggle("rd-padded", sheet);
    if (status === "failed") {
      const state = node("div", "rx-card rx-state");
      state.dataset["testid"] = "rd-failed";
      state.setAttribute("role", "alert");
      state.append(node("h2", undefined, "Your dashboard did not load"), node("p", undefined, "The reports below are still available under All reports."), button("Try again", "rx-btn rx-btn-primary", () => void load(), "rd-retry"));
      element.append(state);
      return;
    }
    if (data !== undefined && data.scope !== "") {
      const scope = node("p", "rd-scope", data.scope);
      scope.dataset["testid"] = "rd-scope";
      element.append(scope);
    }
    if (data !== undefined) {
      const band = attentionBand(data.attention);
      if (band !== undefined) element.append(band);
    }
    // The strip
    const strip = node("section", "rd-block");
    strip.dataset["testid"] = "rd-strip-block";
    if (status === "loading" || data === undefined) {
      strip.append(node("h2", "rx-section", "Right now"));
      const skeleton = node("div", "rx-card rd-strip rd-skeleton");
      skeleton.setAttribute("role", "status");
      skeleton.setAttribute("aria-label", "Loading your dashboard");
      for (let i = 0; i < Math.min(layout.figures.length, MAX_FIGURES); i += 1) skeleton.append(node("div", "rd-cell rd-skel"));
      strip.append(skeleton);
      element.append(strip);
      return;
    }
    const figures = stripFigures();
    if (figures.length + tiles.length > 0) {
      strip.append(node("h2", "rx-section", "Right now"));
      const row = node("div", "rx-card rd-strip");
      row.dataset["testid"] = "rd-strip";
      for (const def of figures) row.append(figureCell(def, (data.figures[def.id] as FigureView)));
      for (const tile of tiles) row.append(tileCell(tile));
      strip.append(row);
      element.append(strip);
    }
    // The previews
    const pins = pinnedPreviews();
    if (pins.length > 0) {
      const block = node("section", "rd-block");
      block.dataset["testid"] = "rd-previews-block";
      block.append(node("h2", "rx-section", "Your pinned reports"));
      const grid = node("div", "rd-previews");
      for (const def of pins) grid.append(previewCard(def, data.previews[def.id] as PreviewView));
      block.append(grid);
      element.append(block);
    }
    if (figures.length + tiles.length === 0 && pins.length === 0) {
      const none = node("div", "rx-card rd-none");
      none.dataset["testid"] = "rd-none";
      none.append(node("b", undefined, "Nothing is pinned to your dashboard"), node("span", undefined, "Choose Customize to add numbers and reports, or open All reports."));
      element.append(none);
    }
    if (sheet) element.append(customizeSheet(data));
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Customize (bottom sheet)
  // -------------------------------------------------------------------------------------------------------------------
  function fitSheet(el: HTMLElement): void {
    const rect = (element.closest(".rx") ?? element).getBoundingClientRect();
    el.style.left = `${Math.max(0, Math.round(rect.left))}px`;
    el.style.width = `${Math.round(rect.width)}px`;
  }
  function row(title: string, hint: string, controls: HTMLElement[], testid: string): HTMLElement {
    const line = node("div", "rd-row");
    line.dataset["testid"] = testid;
    const text = node("div", "rd-row-text");
    text.append(node("b", undefined, title), node("small", undefined, hint));
    line.append(text, ...controls);
    return line;
  }
  function iconButton(label: string, name: string, action: () => void, disabled: boolean, testid: string): HTMLButtonElement {
    const control = button("", "rd-iconbtn", action, testid);
    control.append(icon(name));
    control.setAttribute("aria-label", label);
    control.disabled = disabled;
    return control;
  }
  function customizeSheet(current: DashLoad): HTMLElement {
    void current;
    const panel = node("div", "rd-sheet");
    panel.dataset["testid"] = "rd-sheet";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", "Customize your dashboard");
    fitSheet(panel);
    const head = node("div", "rd-sheet-head");
    head.append(node("h2", undefined, "Customize your dashboard"));
    const tabs = node("div", "rd-seg");
    for (const [id, label] of [["figures", "Numbers"], ["previews", "Report previews"]] as const) {
      const tab = button(label, "rd-seg-btn", () => {
        sheetTab = id;
        render();
        focusSheet(`rd-tab-${id}`);
      }, `rd-tab-${id}`);
      tab.setAttribute("aria-pressed", String(sheetTab === id));
      tabs.append(tab);
    }
    head.append(tabs, node("span", "rx-grow"), button("Done", "rx-btn rx-btn-primary", closeSheet, "rd-sheet-done"));
    panel.append(head);
    const body = node("div", "rd-sheet-body");
    if (sheetTab === "figures") figuresPanel(body);
    else previewsPanel(body);
    panel.append(body);
    const foot = node("div", "rd-sheet-foot");
    const limit = sheetTab === "figures" ? MAX_FIGURES : MAX_PREVIEWS;
    const used = sheetTab === "figures" ? stripCount() : pinnedPreviews().length;
    foot.append(
      node("span", "rd-sheet-count", `${used} of ${limit}. This is your own view on this browser. Changes save as you make them.`),
      node("span", "rx-grow"),
      button("Reset to starter set", "rx-btn", () => {
        sheetNote = "";
        save({ figures: [...source.defaults.figures], previews: [...source.defaults.previews] });
        focusSheet("rd-reset");
      }, "rd-reset"),
    );
    if (sheetNote !== "") {
      const note = node("p", "rx-notice", sheetNote);
      note.setAttribute("role", "status");
      panel.append(note);
    }
    panel.append(foot);
    return panel;
  }
  function focusSheet(testid: string): void {
    queueMicrotask(() => element.querySelector<HTMLElement>(`[data-testid="${testid}"]`)?.focus());
  }
  function figuresPanel(body: HTMLElement): void {
    const onStrip = stripFigures();
    const full = stripCount() >= MAX_FIGURES;
    body.append(node("h3", "rd-sheet-h", "On your strip"));
    if (onStrip.length + tiles.length === 0) body.append(node("p", "rd-sheet-none", "No numbers yet. Add one below."));
    onStrip.forEach((def, index) => {
      body.append(
        row(def.title, def.hint, [
          iconButton(`Move ${def.title} earlier`, "up", () => { save({ ...layout, figures: reorder(layout.figures, onStrip.map((entry) => entry.id), def.id, -1) }); focusSheet(`rd-up-${def.id}`); }, index === 0, `rd-up-${def.id}`),
          iconButton(`Move ${def.title} later`, "down", () => { save({ ...layout, figures: reorder(layout.figures, onStrip.map((entry) => entry.id), def.id, 1) }); focusSheet(`rd-down-${def.id}`); }, index === onStrip.length - 1, `rd-down-${def.id}`),
          iconButton(`Remove ${def.title}`, "minus", () => { save({ ...layout, figures: removeId(layout.figures, def.id) }); focusSheet("rd-sheet-done"); }, false, `rd-remove-${def.id}`),
        ], `rd-row-${def.id}`),
      );
    });
    const canManage = context.tiles?.canManage === true;
    tiles.forEach((tile, index) => {
      const label = tile.item.available ? (tile.item.label ?? "Report") : "No longer available to you";
      const move = async (direction: -1 | 1): Promise<void> => {
        const ids = tiles.map((entry) => entry.item.id);
        const to = index + direction;
        if (to < 0 || to >= ids.length) return;
        const swapped = ids[index] as string;
        ids[index] = ids[to] as string;
        ids[to] = swapped;
        const ok = (await context.tiles?.order(ids)) === true;
        sheetNote = ok ? "" : "Could not move that report. Try again.";
        if (ok) [tiles[index], tiles[to]] = [tiles[to] as TileView, tiles[index] as TileView];
        render();
      };
      const remove = async (): Promise<void> => {
        const ok = (await context.tiles?.remove(tile.item.id)) === true;
        sheetNote = ok ? "" : "Could not unpin that report. Try again.";
        if (ok) tiles = tiles.filter((entry) => entry !== tile);
        render();
      };
      body.append(
        row(label, "A saved report you pinned. Unpin removes the pin, not the report.", canManage ? [
          iconButton(`Move ${label} earlier`, "up", () => void move(-1), index === 0, `rd-tile-up-${tile.item.id}`),
          iconButton(`Move ${label} later`, "down", () => void move(1), index === tiles.length - 1, `rd-tile-down-${tile.item.id}`),
          iconButton(`Unpin ${label}`, "minus", () => void remove(), false, `rd-tile-remove-${tile.item.id}`),
        ] : [], `rd-row-tile-${tile.item.id}`),
      );
    });
    const rest = figureDefs().filter((def) => !layout.figures.includes(def.id));
    body.append(node("h3", "rd-sheet-h", "Available to add"));
    if (rest.length === 0) body.append(node("p", "rd-sheet-none", "Everything available to you is already on your strip."));
    for (const def of rest) {
      const add = button("Add", "rx-btn", () => { save({ ...layout, figures: addId(layout.figures, def.id, MAX_FIGURES) }); focusSheet("rd-sheet-done"); }, `rd-add-${def.id}`);
      add.disabled = full;
      add.setAttribute("aria-label", full ? `Add ${def.title}. Your strip is full.` : `Add ${def.title}`);
      const line = row(def.title, full ? `Your strip is full (${MAX_FIGURES}). Remove one to add this.` : def.hint, [add], `rd-row-${def.id}`);
      body.append(line);
    }
  }
  function previewsPanel(body: HTMLElement): void {
    const pinned = pinnedPreviews();
    const full = pinned.length >= MAX_PREVIEWS;
    body.append(node("h3", "rd-sheet-h", "Pinned reports"));
    if (pinned.length === 0) body.append(node("p", "rd-sheet-none", "No reports pinned. Add one below."));
    pinned.forEach((def, index) => {
      body.append(
        row(def.title, def.hint, [
          iconButton(`Move ${def.title} earlier`, "up", () => { save({ ...layout, previews: reorder(layout.previews, pinned.map((entry) => entry.id), def.id, -1) }); focusSheet(`rd-up-${def.id}`); }, index === 0, `rd-up-${def.id}`),
          iconButton(`Move ${def.title} later`, "down", () => { save({ ...layout, previews: reorder(layout.previews, pinned.map((entry) => entry.id), def.id, 1) }); focusSheet(`rd-down-${def.id}`); }, index === pinned.length - 1, `rd-down-${def.id}`),
          iconButton(`Unpin ${def.title}`, "minus", () => { save({ ...layout, previews: removeId(layout.previews, def.id) }); focusSheet("rd-sheet-done"); }, false, `rd-remove-${def.id}`),
        ], `rd-row-${def.id}`),
      );
    });
    const rest = previewDefs().filter((def) => !layout.previews.includes(def.id));
    body.append(node("h3", "rd-sheet-h", "Available to add"));
    if (rest.length === 0) body.append(node("p", "rd-sheet-none", "Every report preview available to you is already pinned."));
    for (const def of rest) {
      const add = button("Add", "rx-btn", () => { save({ ...layout, previews: addId(layout.previews, def.id, MAX_PREVIEWS) }); focusSheet("rd-sheet-done"); }, `rd-add-${def.id}`);
      add.disabled = full;
      add.setAttribute("aria-label", full ? `Add ${def.title}. Your pinned reports are full.` : `Add ${def.title}`);
      body.append(row(def.title, full ? `Your pinned reports are full (${MAX_PREVIEWS}). Unpin one to add this.` : def.hint, [add], `rd-row-${def.id}`));
    }
  }
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && sheet) closeSheet();
  };
  function openSheet(back?: () => HTMLElement | null): void {
    if (data === undefined) return;
    restoreFocus = back ?? restoreFocus;
    sheet = true;
    sheetNote = "";
    document.addEventListener("keydown", onKey);
    render();
    focusSheet("rd-sheet-done");
  }
  function closeSheet(): void {
    sheet = false;
    document.removeEventListener("keydown", onKey);
    render();
    restoreFocus?.()?.focus();
  }
  const onResize = (): void => {
    if (sheet) render();
  };
  window.addEventListener("resize", onResize);

  return {
    element,
    load,
    customize: openSheet,
    destroy() {
      alive = false;
      generation++;
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      element.replaceChildren();
    },
  };
}
